import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

import { getStudents } from '../services/studentService';
import { getCourses } from '../services/courseService';
import { getExams, getExamsByClassLevel } from '../services/examService';
import { getExamGrades, upsertBulkGrades } from '../services/gradeService';
import { Student, Course, Exam, Grade, GradeCreate } from '../types';
import { useAuth } from '../context/AuthContext';

interface GradeCell {
  value: string;
  isNew: boolean;
  originalValue?: string;
  gradeId?: number;
}

interface GradeTable {
  [studentId: number]: {
    [courseId: number]: GradeCell;
  };
}

const BulkGradeEntry: React.FC = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState<Student[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [exams, setExams] = useState<Exam[]>([]);
  const [existingGrades, setExistingGrades] = useState<Grade[]>([]);
  
  const [selectedClassLevel, setSelectedClassLevel] = useState<string>('');
  const [selectedExam, setSelectedExam] = useState<number | ''>('');
  const [gradeTable, setGradeTable] = useState<GradeTable>({});
  const [maxGrade, setMaxGrade] = useState<number>(100);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [examsLoaded, setExamsLoaded] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  const lastFetchedExamRef = useRef<number | null>(null);

  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Class levels (you might want to fetch this from API)
  const classLevels = [
    'Pre-Primary 1', 'Pre-Primary 2',
    'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5',
    'Grade 6', 'Grade 7', 'Grade 8',
    'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'
  ];

  const fetchStudentsAndCourses = async (classLevel?: string) => {
    const targetClassLevel = classLevel || selectedClassLevel;
    
    console.log('fetchStudentsAndCourses called with:', { classLevel, targetClassLevel, selectedClassLevel });
    
    if (!targetClassLevel || !user?.school_id) {
      showSnackbar('Please select a class level', 'error');
      return;
    }

    try {
      setLoading(true);
      setExamsLoaded(false); // Reset exams loaded state
      
      console.log('Fetching data for class level:', targetClassLevel);
      
      // Fetch students and courses for the selected class level
      const [studentsData, coursesData] = await Promise.all([
        getStudents(user.school_id, undefined, undefined, targetClassLevel),
        getCourses(user.school_id)
      ]);
      
      console.log('Fetched data:', { studentsCount: studentsData.length, coursesCount: coursesData.length });
      
      setStudents(studentsData);
      
      // Filter courses by class level
      const filteredCourses = coursesData.filter(course => 
        course.class_level === targetClassLevel
      );
      setCourses(filteredCourses);
      
      console.log('Filtered courses for', targetClassLevel, ':', filteredCourses.length);
      
      // Show notification about loaded data
      showSnackbar(`Loaded ${studentsData.length} students and ${filteredCourses.length} courses for ${targetClassLevel}`, 'success');
      
      // Fetch exams for the class level
      if (filteredCourses.length > 0) {
        const examsData = await getExamsByClassLevel(targetClassLevel, user.school_id);
        setExams(examsData);
        setExamsLoaded(true); // Mark exams as loaded
        console.log('Fetched exams:', examsData.length);
        
        if (examsData.length > 0) {
          showSnackbar(`Found ${examsData.length} exams for ${targetClassLevel}`, 'info');
        } else {
          showSnackbar(`No exams found for ${targetClassLevel}. You can create exams first.`, 'warning');
        }
      } else {
        setExams([]);
        setExamsLoaded(true); // Mark as loaded even if no exams
        console.log('No courses found, no exams to fetch');
      }
      
      // Initialize grade table
      const table: GradeTable = {};
      studentsData.forEach(student => {
        table[student.id] = {};
        filteredCourses.forEach(course => {
          table[student.id][course.id] = {
            value: '',
            isNew: true
          };
        });
      });
      setGradeTable(table);
      
    } catch (error) {
      console.error('Error fetching students and courses:', error);
      showSnackbar('Error loading data. Please try again.', 'error');
      setExamsLoaded(false); // Reset on error
    } finally {
      setLoading(false);
    }
  };

  const fetchExamData = useCallback(async () => {
    if (!selectedExam) return;
    
    try {
      setLoading(true);
      
      const selectedExamData = exams.find(exam => exam.id === selectedExam);
      if (selectedExamData) {
        setMaxGrade(selectedExamData.total_marks);
        
        // Fetch existing grades for this exam
        const gradesData = await getExamGrades(selectedExamData.id);
        setExistingGrades(gradesData);
        
        // Show notification about loaded grades
        if (gradesData.length > 0) {
          showSnackbar(`Loaded ${gradesData.length} existing grades for ${selectedExamData.title}`, 'info');
        } else {
          showSnackbar(`No existing grades found for ${selectedExamData.title}. Ready for new grade entry.`, 'info');
        }
        
        // Initialize grade table structure if not already done
        const updatedTable = { ...gradeTable };
        
        // Ensure the grade table structure exists for all students and courses
        students.forEach(student => {
          if (!updatedTable[student.id]) {
            updatedTable[student.id] = {};
          }
          courses.forEach(course => {
            if (!updatedTable[student.id][course.id]) {
              updatedTable[student.id][course.id] = {
                value: '',
                isNew: true
              };
            }
          });
        });
        
        // Update grade table with existing grades
        gradesData.forEach(grade => {
          if (updatedTable[grade.student_id] && updatedTable[grade.student_id][grade.course_id]) {
            // Format the grade value consistently to avoid precision issues
            const formattedGrade = parseFloat(grade.grade.toFixed(2));
            updatedTable[grade.student_id][grade.course_id] = {
              value: formattedGrade.toString(),
              isNew: false,
              originalValue: formattedGrade.toString(),
              gradeId: grade.id
            };
          }
        });
        setGradeTable(updatedTable);
      }
      
    } catch (error) {
      console.error('Error fetching exam data:', error);
      showSnackbar('Error loading exam grades. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedExam, exams, students, courses, gradeTable]);

  const handleClassLevelChange = (classLevel: string) => {
    console.log('handleClassLevelChange called with:', classLevel);
    
    // Clear all state first
    setSelectedClassLevel(classLevel);
    setSelectedExam('');
    setStudents([]);
    setCourses([]);
    setExams([]);
    setGradeTable({});
    setExistingGrades([]);
    setExamsLoaded(false); // Reset exams loaded state
    lastFetchedExamRef.current = null; // Reset the ref
    
    // Only fetch data if a class level is selected
    if (classLevel) {
      showSnackbar(`Loading data for ${classLevel}...`, 'info');
      console.log('Calling fetchStudentsAndCourses with:', classLevel);
      fetchStudentsAndCourses(classLevel);
    }
  };

  const handleExamChange = (examId: number) => {
    setSelectedExam(examId);
  };

  const handleGradeChange = (studentId: number, courseId: number, value: string) => {
    setGradeTable(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [courseId]: {
          ...prev[studentId][courseId],
          value
        }
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const gradesToUpsert: (GradeCreate | { id: number; data: any })[] = [];
      let newGradesCount = 0;
      let updatedGradesCount = 0;
      
      Object.entries(gradeTable).forEach(([studentId, courseGrades]) => {
        Object.entries(courseGrades).forEach(([courseId, gradeCell]) => {
          const cell = gradeCell as GradeCell;
          if (cell.value.trim()) {
            const gradeValue = parseFloat(cell.value);
            if (!isNaN(gradeValue) && gradeValue >= 0 && gradeValue <= maxGrade) {
              if (cell.isNew) {
                // Create new grade
                gradesToUpsert.push({
                  student_id: parseInt(studentId),
                  course_id: parseInt(courseId),
                  exam_id: selectedExam as number,
                  assignment_name: exams[0]?.title || 'Exam',
                  grade: gradeValue,
                  max_grade: maxGrade,
                  grade_type: 'exam'
                });
                newGradesCount++;
              } else {
                // Compare numeric values instead of strings to avoid precision issues
                const originalValue = parseFloat(cell.originalValue || '0');
                const currentValue = parseFloat(cell.value);
                
                // Use a small epsilon for floating point comparison
                const epsilon = 0.01;
                if (Math.abs(currentValue - originalValue) > epsilon) {
                  // Update existing grade
                  gradesToUpsert.push({
                    id: cell.gradeId!,
                    data: {
                      grade: gradeValue,
                      max_grade: maxGrade
                    }
                  });
                  updatedGradesCount++;
                }
              }
            }
          }
        });
      });
      
      if (gradesToUpsert.length > 0) {
        await upsertBulkGrades(gradesToUpsert);
        
        // Show detailed success message
        let successMessage = '';
        if (newGradesCount > 0 && updatedGradesCount > 0) {
          successMessage = `Successfully saved ${newGradesCount} new grades and updated ${updatedGradesCount} existing grades`;
        } else if (newGradesCount > 0) {
          successMessage = `Successfully created ${newGradesCount} new grades`;
        } else if (updatedGradesCount > 0) {
          successMessage = `Successfully updated ${updatedGradesCount} existing grades`;
        }
        
        showSnackbar(successMessage, 'success');
        
        // Refresh data
        await fetchExamData();
      } else {
        showSnackbar('No grades to save', 'info');
      }
      
    } catch (error) {
      console.error('Error saving grades:', error);
      showSnackbar('Error saving grades. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSelectedClassLevel('');
    setSelectedExam('');
    setGradeTable({});
    setStudents([]);
    setCourses([]);
    setExams([]);
    setExistingGrades([]);
    setExamsLoaded(false);
    lastFetchedExamRef.current = null; // Reset the ref
    
    showSnackbar('All selections have been reset', 'info');
  };

  const getChangedGradesCount = () => {
    let count = 0;
    Object.values(gradeTable).forEach(courseGrades => {
      Object.values(courseGrades).forEach(gradeCell => {
        const cell = gradeCell as GradeCell;
        if (cell.value.trim()) {
          if (cell.isNew) {
            count++;
          } else {
            // Compare numeric values instead of strings to avoid precision issues
            const originalValue = parseFloat(cell.originalValue || '0');
            const currentValue = parseFloat(cell.value);
            
            // Use a small epsilon for floating point comparison
            const epsilon = 0.01;
            if (Math.abs(currentValue - originalValue) > epsilon) {
              count++;
            }
          }
        }
      });
    });
    return count;
  };

  // Auto-fetch exam data when students, courses, and exam are all available
  useEffect(() => {
    if (selectedExam && students.length > 0 && courses.length > 0 && !loading) {
      // Only fetch if we haven't already fetched for this exam
      if (lastFetchedExamRef.current !== selectedExam) {
        lastFetchedExamRef.current = selectedExam;
        fetchExamData();
      }
    }
  }, [selectedExam, students.length, courses.length, loading]);

  const selectedExamData = exams.find(exam => exam.id === selectedExam);

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Bulk Grade Entry
      </Typography>

      {/* Selection Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <FormControl fullWidth>
                <InputLabel>Class Level</InputLabel>
                <Select
                  value={selectedClassLevel}
                  onChange={(e) => handleClassLevelChange(e.target.value)}
                  label="Class Level"
                >
                  {classLevels.map(level => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <FormControl fullWidth disabled={!selectedClassLevel || !examsLoaded || loading}>
                <InputLabel>Exam</InputLabel>
                <Select
                  value={selectedExam}
                  onChange={(e) => handleExamChange(e.target.value as number)}
                  label="Exam"
                >
                  {loading && !examsLoaded ? (
                    <MenuItem disabled>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        Loading exams...
                      </Box>
                    </MenuItem>
                  ) : (
                    exams.map(exam => (
                      <MenuItem key={exam.id} value={exam.id}>
                        {exam.title}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<CancelIcon />}
              >
                Reset
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  showSnackbar('Refreshing exam data...', 'info');
                  fetchExamData();
                }}
                disabled={!selectedExam}
                startIcon={<RefreshIcon />}
              >
                Refresh
              </Button>
            </Box>
          </Box>
          
          {selectedExamData && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Exam:</strong> {selectedExamData.title} | 
                <strong>Total Marks:</strong> {selectedExamData.total_marks} | 
                <strong>Passing Marks:</strong> {selectedExamData.passing_marks} | 
                <strong>Duration:</strong> {selectedExamData.duration_minutes} minutes
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Instructions */}
      {!selectedExam && selectedClassLevel && !loading && examsLoaded && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Please select an exam to proceed with grade entry.
        </Alert>
      )}

      {/* Loading state */}
      {selectedClassLevel && loading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            Loading students, courses, and exams for {selectedClassLevel}...
          </Box>
        </Alert>
      )}

      {/* No courses found */}
      {selectedClassLevel && courses.length === 0 && !loading && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No courses found for the selected class level ({selectedClassLevel}). Please create courses for this class level first.
        </Alert>
      )}

      {/* No exams found */}
      {selectedClassLevel && courses.length > 0 && exams.length === 0 && !loading && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No exams found for {selectedClassLevel}. Please create an exam for this class level first.
        </Alert>
      )}

      {/* Grade Table */}
      {selectedExam && students.length > 0 && courses.length > 0 && (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Grade Entry Table ({students.length} students, {courses.length} courses)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip 
                  label={`${getChangedGradesCount()} grades to save`} 
                  color="primary" 
                  variant="outlined"
                />
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || getChangedGradesCount() === 0}
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                  {saving ? 'Saving...' : 'Save All Grades'}
                </Button>
              </Box>
            </Box>
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 200, fontWeight: 'bold', position: 'sticky', left: 0, zIndex: 2, backgroundColor: 'background.paper' }}>
                    Student Name
                  </TableCell>
                  {courses.map(course => (
                    <TableCell key={course.id} align="center" sx={{ minWidth: 100, fontWeight: 'bold' }}>
                      {course.title}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {students.map(student => (
                  <TableRow key={student.id} hover>
                    <TableCell sx={{ fontWeight: 'bold', position: 'sticky', left: 0, zIndex: 1, backgroundColor: 'background.paper' }}>
                      {student.name}
                    </TableCell>
                    {courses.map(course => {
                      const gradeCell = gradeTable[student.id]?.[course.id] || {
                        value: '',
                        isNew: true
                      };
                      
                      // Use numeric comparison for visual indicator consistency
                      let hasChanges = gradeCell.isNew;
                      if (!gradeCell.isNew && gradeCell.originalValue && gradeCell.value.trim()) {
                        const originalValue = parseFloat(gradeCell.originalValue || '0');
                        const currentValue = parseFloat(gradeCell.value);
                        const epsilon = 0.01;
                        hasChanges = Math.abs(currentValue - originalValue) > epsilon;
                      }
                      
                      return (
                        <TableCell key={course.id} align="center">
                          <TextField
                            size="small"
                            value={gradeCell.value}
                            onChange={(e) => handleGradeChange(student.id, course.id, e.target.value)}
                            inputProps={{
                              min: 0,
                              max: maxGrade,
                              step: 0.1,
                              style: { textAlign: 'center' }
                            }}
                            sx={{
                              width: 80,
                              '& .MuiInputBase-input': {
                                textAlign: 'center',
                                backgroundColor: hasChanges ? '#fff3cd' : 'transparent'
                              }
                            }}
                            placeholder={`0-${maxGrade}`}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default BulkGradeEntry;